import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { FormInputWrapper } from "@insite/mobius/FormField";
import { HiddenProps } from "@insite/mobius/Hidden";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Search from "@insite/mobius/Icons/Search";
import X from "@insite/mobius/Icons/X";
import { OverflowMenuPresentationProps } from "@insite/mobius/OverflowMenu";
import TextField, { TextFieldProps } from "@insite/mobius/TextField";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useState, useEffect } from "react";
import styled, { css } from "styled-components";
import ADIColors from "@NA/Utils/ADIColors";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";

interface OwnProps {
    searchText?: string;
    onSearchTextChanged: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onSearch: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
    allowCreateAddress?: boolean;
    onCreateNewAddressClick?: React.EventHandler<React.MouseEvent>;
    isSearchDisabled?: boolean;
    extendedStyles?: ADICustomerSelectorToolbarStyles;
    searchResult?: any;
    isSearchClicked?: any;
}

export interface ADICustomerSelectorToolbarStyles {
    wrapper?: InjectableCss;
    searchTextField?: TextFieldProps;
    searchButton?: ButtonPresentationProps;
    wideHidden?: HiddenProps;
    wideCreateNewAddressButton?: ButtonPresentationProps;
    narrowHidden?: HiddenProps;
    overflowMenu?: OverflowMenuPresentationProps;
    narrowCreateNewAddressClickable?: ClickablePresentationProps;
    searchTextFieldContainer?: InjectableCss;
    searchIconStyles?: IconPresentationProps;
    closeIconStyles?: IconPresentationProps;
    iconsContainer?: InjectableCss;
    closeContainer?: InjectableCss;
    pleaseSelectStyles?: InjectableCss;
}

export const adiCustomerSelectorToolbarStyles: ADICustomerSelectorToolbarStyles = {
    pleaseSelectStyles: {
        css: css`
        font-size: 14px;
        line-height: 1.5em;
        `
    },
    iconsContainer: {
        css: css`
        display: flex;
        align-items: center;
        `
    },
    searchIconStyles: {
        css: css`
        transform: rotate(90deg);
        margin-left: 8px;
        cursor: pointer;
        svg{
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        height: 24px;
                        width: 24px;
                        margin-left: 2px;
                        `,
                    css`
                        height: 24px;
                        width: 24px;
                        margin-left: 2px;
                        `,
                    null,
                    null,
                    null,
                ])}
            
        }
        `
    },
    closeIconStyles: {
        css: css`
        padding-right: 4px;
        border-right: 1px solid ${ADIColors.gray3};
        color: ${ADIColors.gray3};
        cursor: pointer;
        height: 18px;
        svg {
            color: ${ADIColors.gray4};
            stroke-width: 1;
        }
        `
    },
    searchTextFieldContainer: {
        css: css`
        display: flex;
        flex-direction: row;
        border: 1px solid ${ADIColors.gray3};
        padding: 0 10px 0 5px;
        margin-bottom: 14px;
        border-radius: 2px;
        ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                padding: 0 5px;
            `,
                    css`
                padding: 0 5px;
            `,
                    null,
                    null,
                    null,
                ])}
        `
    },
    wrapper: {
        css: css`
            display: flex;
            padding-bottom: 9px;
            flex-direction: column;
        `,
    },
    searchTextField: {
        cssOverrides: {
            formField: css`
                flex-basis: 100%;
                >${FormInputWrapper}{
                    margin-top: 0px !important;
                }
                input{
                    border: none !important;
                    padding: 9px 15px 9px 9px !important;
                    height: 35px;
                    &:focus{
                        border: none !important;
                        outline: none !important;
                        box-shadow: none !important;
                    }
                    ::placeholder {
                        color: ${ADIColors.gray3};
                        opacity: 1;
                    }
    
                    ::-ms-input-placeholder {
                        color: ${ADIColors.gray3};
                    }
                }
            `,
        },
    },
    searchButton: {
        variant: "primary",
        css: css`
            margin-left: auto;
        `,
    },
    wideHidden: { below: "md" },
    wideCreateNewAddressButton: {
        variant: "tertiary",
        css: css`
            margin-left: 10px;
        `,
    },
    narrowHidden: { above: "sm" },
    overflowMenu: {
        cssOverrides: {
            wrapper: css`
                margin-left: 10px;
            `,
        },
    },
};

const ToolbarStyled = styled.div<InjectableCss>`
    ${({ css }) => css}
`;

const ADICustomerSelectorToolbar: FC<OwnProps> = ({
    extendedStyles,
    searchText,
    onSearchTextChanged,
    onSearch,
    searchResult,
    isSearchClicked
}) => {
    const [styles] = useState(() => mergeToNew(adiCustomerSelectorToolbarStyles, extendedStyles));
    const [isCloseVisible, setIsCloseVisible] = useState(false);
    const [noSearchResult, setNoSearchResult] = useState(false)

    useEffect(() => {
        if (isSearchClicked) {
            if (searchResult?.length <= 1) {
                setNoSearchResult(true)
            }
        }
        else {
            setNoSearchResult(false)
        }
    }, [onSearch, isCloseVisible])

    const clearInput = () => {
        onSearchTextChanged({ currentTarget: { value: "" } } as React.ChangeEvent<HTMLInputElement>); // Clear the input text
        setIsCloseVisible(false);
    };

    const onSearchEnterPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter' || event.keyCode === 13) {
            event.preventDefault();
            const syntheticEvent = new MouseEvent("click", {
                bubbles: true,
                cancelable: true,
                view: window,
            });
            const mouseEvent = syntheticEvent as unknown as React.MouseEvent<HTMLButtonElement, MouseEvent>;
            onSearch(mouseEvent);
        }
    }

    return (
        <ToolbarStyled {...styles.wrapper}>
            <StyledWrapper {...styles.searchTextFieldContainer}>
                <TextField
                    {...styles.searchTextField}
                    placeholder="Search address on file"
                    value={searchText}
                    onChange={(e) => {
                        onSearchTextChanged(e);
                        setIsCloseVisible(!!e.target.value);
                    }}
                    onKeyDown={onSearchEnterPress}
                    aria-label={translate("enter search term")}
                />
                <StyledWrapper {...styles.iconsContainer}>
                    {isCloseVisible && searchText && (
                        <Icon onClick={clearInput} {...styles.closeIconStyles} src={X} />
                    )}
                    <Icon {...styles.searchIconStyles} src={Search}
                        onClick={onSearch} />
                </StyledWrapper>
            </StyledWrapper>
            {noSearchResult ? (
                <Typography as="p" {...styles.pleaseSelectStyles}>
                    {translate(`No results found for "${searchText}"`)}
                </Typography>
            ) : (
                searchResult && searchResult.length > 2 && (
                    <Typography as="p" {...styles.pleaseSelectStyles}>
                        {"Please select the address."}
                    </Typography>
                )
            )}
        </ToolbarStyled>
    );
};

export default ADICustomerSelectorToolbar;
